import { DashboardsAPI } from '@/API/Dashboards'
import type { AppDispatch, RootState } from '@/redux'
import { dashboardPanelsActions, selectPanelIds } from '@/redux/slice/dashboardPanelsSlice'
import { dashboardSyncActions } from '@/redux/slice/dashboardSyncSlice'
import { dashboardViewActions } from '@/redux/slice/dashboardViewSlice'
import type { SyncDashboardCompletePayload, SyncSourceResult } from '@/types/Dashboards.types'
import type { SyncErrorEnvelope, SyncErrorCode } from '@/types/SyncError.types'
import { isSyncErrorEnvelope } from '@/types/SyncError.types'

/**
 * Priority ranking for selecting the worst-case envelope when multiple sources fail.
 *
 * Priority order (highest = most urgent, matches TDD §6.1 note):
 *   RATE_LIMIT > AUTH_EXPIRED > SCHEMA_DRIFT > TRANSIENT_5XX > NETWORK > PLATFORM > BUSINESS_EMPTY
 *
 * Rationale: RATE_LIMIT requires the user to wait and blocks further syncing —
 * the user must know immediately. AUTH_EXPIRED requires a reconnect CTA.
 * SCHEMA_DRIFT signals a data-integrity issue. Everything else is transient.
 */
const SYNC_ERROR_CODE_PRIORITY: Record<SyncErrorCode, number> = {
  RATE_LIMIT: 7,
  AUTH_EXPIRED: 6,
  // AUTH_INVALID shares AUTH_EXPIRED's priority — both block sync until
  // the user reconnects. Slice F bugbash 2026-05-06.
  AUTH_INVALID: 6,
  SCHEMA_DRIFT: 5,
  TRANSIENT_5XX: 4,
  NETWORK: 3,
  PLATFORM: 2,
  BUSINESS_EMPTY: 1,
}

/**
 * Given an array of failed source results, picks the worst-case envelope.
 * Prefers the typed `errorEnvelope` field; synthesizes a PLATFORM envelope
 * from the legacy `error: string` field as a defensive fallback.
 *
 * Returns null if no failed source has an actionable error.
 */
export function pickWorstCaseEnvelope(
  failedSources: SyncSourceResult[]
): SyncErrorEnvelope | null {
  let best: SyncErrorEnvelope | null = null
  let bestPriority = -1

  for (const source of failedSources) {
    let envelope: SyncErrorEnvelope | null = null

    if (isSyncErrorEnvelope(source.errorEnvelope)) {
      envelope = source.errorEnvelope
    } else if (typeof source.error === 'string') {
      // Legacy fallback: synthesize a PLATFORM envelope from the error string.
      // The credential heuristic (/credential/i) is intentionally NOT carried
      // forward here — AUTH_EXPIRED must come from the BE envelope, not a
      // string-match heuristic. (Plan §3 D1 — no silent misclassification.)
      envelope = {
        code: 'PLATFORM',
        message: source.error,
        vendor: source.appName,
      }
    }

    if (envelope) {
      const priority = SYNC_ERROR_CODE_PRIORITY[envelope.code] ?? 0
      if (priority > bestPriority) {
        best = envelope
        bestPriority = priority
      }
    }
  }

  return best
}

/**
 * Builds a human-readable summary string for the post-sync toast.
 *
 * Rules:
 * 1. All fresh  → "All data is fresh (synced within the last hour)."
 * 2. All success → "All N sources refreshed."
 * 3. Otherwise  → join per-source summaries with " · "
 *    - success → "{appName} refreshed"
 *    - fresh   → "{appName} was already fresh"
 *    - error   → "{appName} failed"
 */
export function buildSyncSummary(results: SyncSourceResult[]): string {
  if (results.length === 0) return 'Sync complete.'

  const allFresh = results.every(r => r.status === 'fresh')
  if (allFresh) return 'All data is fresh (synced within the last hour).'

  const allSuccess = results.every(r => r.status === 'success')
  if (allSuccess) return `All ${results.length} source${results.length === 1 ? '' : 's'} refreshed.`

  const parts = results.map(r => {
    if (r.status === 'success') return `${r.appName} refreshed`
    if (r.status === 'fresh') return `${r.appName} was already fresh`
    return `${r.appName} failed`
  })

  return parts.join(' · ')
}

/**
 * Triggers a dashboard sync on the backend.
 * Sets isSyncing = true and clears any prior syncError.
 * isSyncing remains true until sync:dashboard:complete arrives via Socket.IO.
 * On alreadySyncing: true, silently clears the loading state (no error).
 */
export const triggerSync = (
  token: string,
  dashboardId: string,
  options?: { forceRefresh?: boolean }
) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    dispatch(dashboardSyncActions.setLastSyncResults([]))
    dispatch(dashboardSyncActions.setSyncing(true))
    dispatch(dashboardSyncActions.setSyncError(null))

    const resp = await DashboardsAPI.triggerSync(token, dashboardId, options)

    if (resp.err || !resp.data) {
      dispatch(dashboardSyncActions.setSyncing(false))
      dispatch(dashboardSyncActions.setSyncError(resp.err ?? 'Failed to trigger sync.'))
      return
    }

    if (resp.data.alreadySyncing) {
      dispatch(dashboardSyncActions.setSyncing(false))
      // Silently do nothing — sync button returns to default state
      return
    }

    // Stale-dashboard guard: if the user navigated away while the API call was
    // in-flight, getState() now reflects a different dashboard. Do not initialise
    // insight loading for panels that belong to a different route.
    const currentId = getState().dashboards.selectedDashboard?.id
    if (currentId !== dashboardId) return

    // Only dispatch insight loading after confirming a sync was actually started.
    // Reading panel IDs from state at this point (post-API-call) is intentional —
    // see TDD Risk #4: do not dispatch before the alreadySyncing guard.
    const panelIds = selectPanelIds(getState())
    dispatch(dashboardPanelsActions.setSyncInsightsLoading(panelIds))

    // isSyncing remains true — cleared by handleSyncComplete when
    // sync:dashboard:complete arrives via Socket.IO.
  }

// Module-level serial counter — ensures only the last in-flight loadDashboardData
// call clears the loading state. Concurrent/overlapping calls increment this counter;
// older calls detect they are stale in the finally block and skip setIsPanelDataLoading(false).
let panelDataLoadSerial = 0

/** Exported for test isolation only — resets the serial counter between test runs. */
export function _resetPanelDataLoadSerial(): void {
  panelDataLoadSerial = 0
}

/**
 * Loads all panel data for a dashboard from the batch endpoint.
 * Drops stale responses if the active dashboard has changed.
 * Uses a serial counter to prevent concurrent calls from prematurely clearing loading state.
 */
export const loadDashboardData = (
  token: string,
  dashboardId: string
) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const mySerial = ++panelDataLoadSerial
    dispatch(dashboardSyncActions.setIsPanelDataLoading(true))
    dispatch(dashboardSyncActions.setPanelDataError(null))

    try {
      const resp = await DashboardsAPI.getDashboardData(token, dashboardId)

      // Stale response guard — dashboard switched
      const currentId = getState().dashboards.selectedDashboard?.id
      if (currentId !== dashboardId) return

      if (resp.err || !resp.data) {
        dispatch(dashboardSyncActions.setPanelDataError(resp.err ?? 'Failed to load panel data.'))
        return
      }

      dispatch(dashboardSyncActions.setPanelData(resp.data.panels))
    } catch {
      dispatch(dashboardSyncActions.setPanelDataError('Failed to load panel data.'))
    } finally {
      // Only clear loading if this is the most recent request
      if (mySerial === panelDataLoadSerial) {
        dispatch(dashboardSyncActions.setIsPanelDataLoading(false))
      }
    }
  }

/**
 * Handles the sync:dashboard:complete Socket.IO event.
 * Clears isSyncing, stores results for toast, routes each error envelope to its
 * independent slot in dashboardViewSlice (multi-banner stacking rule, §34.3),
 * then re-fetches panel data.
 *
 * The worst-case picker is no longer used here — each envelope is routed
 * independently so multiple concurrent error states are all visible on the canvas.
 */
export const handleSyncComplete = (
  token: string,
  dashboardId: string,
  payload: SyncDashboardCompletePayload
) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    dispatch(dashboardSyncActions.setLastSyncResults(payload.results))
    dispatch(dashboardSyncActions.setSyncing(false))
    // Clear any insight loading flags that were set at sync-start. This is the
    // safety net for panels whose socket insight events never arrive (disconnect,
    // server omission, etc.) — without this, the skeleton would show indefinitely.
    dispatch(dashboardPanelsActions.clearSyncInsightsLoading(selectPanelIds(getState())))

    if (payload.overallStatus === 'partial' || payload.overallStatus === 'error') {
      const failedSources = payload.results.filter(r => r.status === 'error')

      // Multi-banner stacking rule (CEO directive 2026-05-05, brand guide §34.3):
      // Route EVERY envelope to its independent slot — no worst-case collapsing.
      // setDashboardError reducer routes each code to rateLimitedUntil,
      // authExpiredEnvelope, or domainErrorStack based on envelope.code.
      for (const source of failedSources) {
        let envelope: SyncErrorEnvelope | null = null
        if (isSyncErrorEnvelope(source.errorEnvelope)) {
          envelope = source.errorEnvelope
        } else if (typeof source.error === 'string') {
          // Legacy fallback: synthesize a PLATFORM envelope from the error string.
          // AUTH_EXPIRED must arrive from a typed BE envelope — no string heuristic.
          envelope = {
            code: 'PLATFORM',
            message: source.error,
            vendor: source.appName,
          }
        }
        if (envelope) {
          dispatch(dashboardViewActions.setDashboardError(envelope))
        }
      }

      // Legacy compat shim: also dispatch setSyncError(string) for consumers
      // (dashboardSyncSlice.syncError) that have not yet migrated to the envelope.
      // TODO (cleanup): remove once all consumers read from dashboardViewSlice.
      const isCredentialError = (error?: string) =>
        error != null && /credential/i.test(error)
      let errorMessage: string
      if (failedSources.length === 1) {
        const src = failedSources[0]
        errorMessage = isCredentialError(src.error)
          ? `Sync failed for ${src.appName} — credentials may be expired.`
          : `Sync failed for ${src.appName} — please try again.`
      } else if (failedSources.length >= 2) {
        const names = failedSources.slice(0, 2).map(s => s.appName).join(' and ')
        const anyCredential = failedSources.some(s => isCredentialError(s.error))
        errorMessage = anyCredential
          ? `Sync failed for ${names} — credentials may be expired.`
          : `Sync failed for ${names} — please try again.`
      } else {
        errorMessage = 'Sync failed — please try again.'
      }
      dispatch(dashboardSyncActions.setSyncError(errorMessage))
    } else {
      // Success or no errors — clear all three error slots.
      dispatch(dashboardViewActions.setDashboardError(null))
    }

    // Re-fetch panel data now that sync is complete
    await dispatch(loadDashboardData(token, dashboardId))
  }
